import { PerAdultLabel } from '@/src/components/common/srp/perAdultLabel'
import { Plan } from '@/src/components/desktop/srp/fareFamily/types'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { Alert, Box, Button, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React from 'react'

type FareFamilyFooterProps = {
    handleFareFamilyBookClick: (e: React.MouseEvent<HTMLButtonElement>) => void
    isCreateItineraryLoading?: boolean
    selectedPlan: Plan
    /** CTA label override (e.g. "Book now" on the final tab of the split-view two-leg popup). Defaults to "Continue". */
    actionLabel?: string
    /** Resolved price to show instead of `selectedPlan.displayPrice` — the RT split view's final tab uses
     *  it to show the onward+return SUM (plain fares) or the special combo's round-trip total. */
    displayPriceOverride?: string
    /** Retail round-trip sum struck through next to the price (RT split view, special combo applies). */
    strikeOffPrice?: string
    /** Show the "Round trip total price" label under the price (RT split view's final tab). */
    showRoundTripLabel?: boolean
    /** Advisory note under the price — RT split view uses it for "review all fare benefits in the next step",
     *  since each leg's Regular card carries the special combo's benefits regardless of the other leg's pick. */
    note?: string
    disableCoupon?: boolean
}

const FareFamilyFooter = ({
    selectedPlan,
    handleFareFamilyBookClick,
    isCreateItineraryLoading,
    actionLabel,
    displayPriceOverride,
    strikeOffPrice,
    showRoundTripLabel,
    note,
    disableCoupon = false
}: FareFamilyFooterProps) => {
    const t = useTranslations()
    const { selectedJourneyLeg } = useSearchResults()
    return (
        <Box width="100%">
            {/* Coupon Banner */}
            {/* {selectedPlan.benefitsText.exclusiveBenefits && (
                <Box
                    sx={{
                        width: '100%',
                        backgroundColor: '#00c03f',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '14px',
                        px: 1.5,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <LocalOfferIcon sx={{ color: 'white', fontSize: 20 }} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                        {selectedPlan.benefitsText.exclusiveBenefits}
                    </Typography>
                </Box>
            )} */}

            {/* Advisory banner above the price row — qualifies what the user is about to book. */}
            {!!note && (
                <Alert severity="info" variant='standard' sx={{ p: 0, bgcolor: "transparent", border: "none", fontSize: "13px" }}>
                    {note}
                </Alert>
            )}

            {/* Price + Book Button */}
            <Box
                sx={{
                    width: '100%',
                    px: '8px',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box>
                    {showRoundTripLabel && (
                        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                            {t('common.roundTripTotalPrice')}
                        </Typography>
                    )}
                    <Box display="flex" alignItems="baseline" gap={0.75}>
                        {!!strikeOffPrice && (
                            <Typography
                                sx={{ fontSize: '13px', color: 'grey.500', textDecoration: 'line-through' }}
                            >
                                {strikeOffPrice}
                            </Typography>
                        )}
                        <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                            {displayPriceOverride ? (
                                displayPriceOverride
                            ) : (
                                <>
                                    {!!selectedPlan.planPricePrefix && selectedPlan.planPricePrefix} {selectedPlan.displayPrice}{' '}
                                    {selectedPlan.isPerPaxPricingDefined && <PerAdultLabel />}
                                </>
                            )}
                        </Typography>
                    </Box>

                    {selectedJourneyLeg == 0 && !disableCoupon && (
                        <Typography sx={{ fontSize: '12px', color: 'rgba(17, 163, 68, 1)' }}>
                            {selectedPlan.benefitsText.exclusiveBenefits}
                        </Typography>
                    )}
                </Box>

                <Button
                    onClick={handleFareFamilyBookClick}
                    variant="customVariantPrimary"
                    disabled={isCreateItineraryLoading}
                    sx={theme => ({
                        // backgroundColor: theme.palette.primary.main,
                        borderRadius: '0.5rem',
                        px: 5,
                        py: 1.2,
                        boxShadow: 'none',
                        // '&:hover': {
                        //     backgroundColor: theme.palette.secondary.main,
                        //     color: theme.palette.primary.main,
                        //     boxShadow: 'none'
                        // }
                    })}
                >
                    {isCreateItineraryLoading ? t('common.Loading') : (actionLabel ?? t('common.Continue'))}
                </Button>
            </Box>
        </Box>
    )
}

export default FareFamilyFooter
